import { Injectable } from "@angular/core";
import { Observable } from "rxjs";

import {
	ConsumerDataRightsGroupResponse,
	ConsumerDataRightsGroupsAssignmentRequest,
	DataRightsGroup,
	DataRightsGroupMember,
	DataRightsGroupMembershipRequest,
	DataRightsGroupRequest,
	DataRightsGroupSearchCriteria,
	DataRightsGroupSummary,
	DataRightsSummary,
	PaginatedDataRightsGroupResponse
} from "../../definitions/data-rights";
import { AppConfigService } from "../app.config.service";
import { ApiService } from "./api.service";
import { BaseApiService } from "./base-api.service";

@Injectable({ providedIn: "root" })
export class DataRightsGroupService extends BaseApiService {
	private readonly apiEndpoint: string;

	constructor(
		private api: ApiService,
		appConfigService: AppConfigService
	) {
		const apiEndpoint: string = appConfigService.getConfigValue("API_ENDPOINT");
		super("mpapi/admin/data-rights-groups", apiEndpoint);
		this.apiEndpoint = apiEndpoint;
	}

	// ========================================================================
	// Group CRUD
	// ========================================================================

	search(
		criteria: DataRightsGroupSearchCriteria
	): Observable<PaginatedDataRightsGroupResponse> {
		return this.api.postAndThrowError(this.url("/search"), criteria);
	}

	getById(id: string): Observable<DataRightsGroup> {
		return this.api.getAndThrowError(this.url(`/${id}`));
	}

	create(request: DataRightsGroupRequest): Observable<DataRightsGroup> {
		return this.api.postAndThrowError(this.url(), request);
	}

	update(id: string, request: DataRightsGroupRequest): Observable<DataRightsGroup> {
		return this.api.putAndThrow(this.url(`/${id}`), request);
	}

	delete(id: string): Observable<void> {
		return this.api.delete(this.url(`/${id}`));
	}

	promoteDefault(id: string): Observable<DataRightsGroup> {
		return this.api.postAndThrowError(this.url(`/${id}/default`), {});
	}

	setMembership(id: string, members: DataRightsGroupMember[]): Observable<DataRightsGroup> {
		const body: DataRightsGroupMembershipRequest = { members };
		return this.api.putAndThrow(this.url(`/${id}/data-rights`), body);
	}

	// ========================================================================
	// Consumer assignments
	// ========================================================================

	getConsumerGroups(consumerId: string): Observable<DataRightsGroupSummary[]> {
		return this.api.getAndThrowError(
			`${this.apiEndpoint}mpapi/admin/consumers/${consumerId}/data-rights-groups`
		);
	}

	setConsumerGroups(
		consumerId: string,
		dataRightsGroupIds: string[]
	): Observable<DataRightsGroupSummary[]> {
		const body: ConsumerDataRightsGroupsAssignmentRequest = { dataRightsGroupIds };
		return this.api.putAndThrow(
			`${this.apiEndpoint}mpapi/admin/consumers/${consumerId}/data-rights-groups`,
			body
		);
	}

	// ========================================================================
	// Consumer-scoped dropdown source (PO wizard)
	// ========================================================================

	getAvailableDataRightsForCurrentConsumer(): Observable<DataRightsSummary[]> {
		return this.api.getAndThrowError(
			`${this.apiEndpoint}mpapi/v2/consumer/available-data-rights`
		);
	}

	getAvailableDataRightsGroupsForCurrentConsumer(): Observable<
		ConsumerDataRightsGroupResponse[]
	> {
		return this.api.getAndThrowError(
			`${this.apiEndpoint}mpapi/v2/consumer/available-data-rights-groups`
		);
	}
}
